import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Heart, BookOpen, Repeat, Music, Users, ShieldCheck, Download, Church, Lock, Check,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import "./Pricing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHY_CARDS = [
  { icon: BookOpen, title: "Preserve institutional memory", body: "Your parish's customs, preferences and history live on — beyond any single tenure." },
  { icon: Repeat, title: "Reduce repetitive administrative work", body: "Stop rebuilding the same plans, lists and reports from scratch every week." },
  { icon: Music, title: "Strengthen worship planning", body: "Hymns, propers and seasonal arcs planned with your parish's own voice." },
  { icon: Users, title: "Support leadership transitions", body: "New clergy and musicians inherit knowledge, not a blank binder." },
  { icon: ShieldCheck, title: "Secure parish-owned data", body: "Tenant-isolated, role-guarded, audit-logged — protected by design." },
  { icon: Download, title: "Export the complete parish archive at any time", body: "One click produces an encrypted archive of everything your parish has created." },
  { icon: Church, title: "Built specifically for churches", body: "Not adapted from business software — designed for liturgy, ministry and parish life." },
];

function TierCTA({ tier, plans, user, navigate, busy, setBusy, setError }) {
  const startCheckout = async (plan) => {
    if (!user) { navigate(`/register?plan=${plan}`); return; }
    setBusy(plan); setError("");
    try {
      const { data } = await axios.post(`${API}/billing/checkout`, { plan, origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy("");
    }
  };

  if (tier.checkoutPlan) {
    const isCurrent = user?.entitlements?.plan_status === "active" && user?.entitlements?.plan === tier.checkoutPlan;
    return (
      <button className={`mp-btn${tier.flagship ? " gold" : ""}`} disabled={busy === tier.checkoutPlan || isCurrent}
        onClick={() => startCheckout(tier.checkoutPlan)} data-testid={`mp-cta-${tier.key}`}>
        {isCurrent ? "Current Plan" : busy === tier.checkoutPlan ? "Redirecting…" : tier.cta}
      </button>
    );
  }
  if (tier.conversation) {
    return (
      <button className="mp-btn ghost" onClick={() => navigate("/diocese")} data-testid={`mp-cta-${tier.key}`}>
        {tier.cta}
      </button>
    );
  }
  return null;
}

function InterestForm({ user }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/pricing/interest`, { email, tier: "parish_essentials", name: user?.name || "" });
      setDone(true);
    } catch {
      setErr("Please enter a valid email address.");
    } finally { setBusy(false); }
  };

  if (done) return <div className="mp-interest-done" data-testid="essentials-interest-done">✓ You're on the list — we'll be in touch when Parish Essentials launches.</div>;
  if (!open) {
    return (
      <button className="mp-btn ghost" onClick={() => setOpen(true)} data-testid="mp-cta-essentials">
        Join Early Access
      </button>
    );
  }
  return (
    <form className="mp-interest-form" onSubmit={submit} data-testid="essentials-interest-form">
      <input type="email" required placeholder="you@parish.org" value={email}
        onChange={(e) => setEmail(e.target.value)} data-testid="essentials-interest-email" />
      <button className="mp-btn gold" type="submit" disabled={busy} data-testid="essentials-interest-submit">
        {busy ? "Adding…" : "Request Early Access"}
      </button>
      {err && <span className="mp-interest-err">{err}</span>}
    </form>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data)).catch(() => setPlans({}));
  }, []);

  const price = (key, suffix) => {
    const p = plans?.[key];
    if (!p) return "";
    return `$${Math.round(p.amount / 100)}${suffix || ""}`;
  };

  const TIERS = [
    {
      key: "individual", name: "Individual",
      product: "SundayBuilder™",
      priceLine: plans ? price("single_report", " · per report") : " ",
      audience: "Perfect for clergy, musicians, seminarians and consultants.",
      includes: ["SundayBuilder™ worship plans", "PDF & DOCX export", "Saved permanently to your account"],
      cta: "Start Planning", checkoutPlan: "single_report",
    },
    {
      key: "essentials", name: "Parish Essentials",
      priceLine: "Launching Soon", launching: true,
      audience: "Begin preserving your parish's institutional memory.",
      includes: ["SundayBuilder™", "Parish Memory™", "Parish Intelligence™", "Report Center", "Secure parish archive"],
      interest: true,
    },
    {
      key: "pro", name: "Parish Pro", flagship: true,
      priceLine: plans ? price("parish_pro", "/month") : " ",
      audience: "Our flagship platform for growing churches.",
      includes: ["Everything in Parish Essentials", "Musical Intelligence™", "Annual Planning", "Priority Ordo AI™ processing", "Enhanced reporting", "Future ministry dashboards"],
      cta: "Begin Parish Pro", checkoutPlan: "parish_pro",
    },
    {
      key: "cathedral", name: "Cathedral Edition",
      priceLine: "Custom",
      audience: "Designed for complex worship ministries.",
      includes: ["Everything in Parish Pro", "Advanced reporting", "Priority support", "Custom onboarding", "Future multi-user ministry collaboration"],
      cta: "Schedule a Conversation", conversation: true, wide: true,
    },
    {
      key: "diocese", name: "Diocese Edition",
      priceLine: "Custom Engagement",
      audience: "One intelligence platform for every parish in your diocese.",
      includes: ["Diocesan dashboard", "Multi-parish reporting", "Parish analytics", "Shared resources", "Regional benchmarking", "Dedicated implementation", "Executive onboarding"],
      cta: "Request a Consultation", conversation: true, dark: true, wide: true,
    },
  ];

  return (
    <div className="pricing" data-testid="pricing-page">
      <header className="pricing-top">
        <button className="pricing-brand" onClick={() => navigate("/")}>Liturgical Intelligence™</button>
        <div className="pricing-nav">
          {user ? (
            <button onClick={() => navigate("/dashboard")} data-testid="pricing-dashboard">Dashboard</button>
          ) : (
            <>
              <button onClick={() => navigate("/login")} data-testid="pricing-signin">Sign in</button>
              <button className="primary" onClick={() => navigate("/register")} data-testid="pricing-getstarted">Get Started</button>
            </>
          )}
        </div>
      </header>

      {/* Altar-frontal architectural header — the primary visual anchor
          of every page in the platform. Restores the sculpted bas-relief
          identity to the pricing surface. */}
      <section className="li-altar-header pricing-altar" data-testid="pricing-altar">
        <div className="li-altar-header__body">
          <div className="li-altar-header__eyebrow">The Parish Operating System™</div>
          <h1 className="li-altar-header__title">Choose the path that fits your ministry.</h1>
          <p className="li-altar-header__sub">
            Liturgical Intelligence™ helps churches preserve institutional memory, strengthen worship,
            reduce administrative burden, and support every ministry through one intelligent platform.
          </p>
        </div>
      </section>

      <div className="mp-trust-banner" data-testid="trust-banner">
        {["Parish-owned data", "Export anytime", "AES-256 encrypted", "Built specifically for churches", "Powered by Ordo AI™"].map((t) => (
          <span key={t}><Check size={13} /> {t}</span>
        ))}
      </div>

      <h2 className="mp-section-title" data-testid="pathway-title">Choose Your Ministry Path</h2>

      {error && <div className="mp-error" data-testid="mp-error">{error}</div>}

      <div className="mp-grid" data-testid="ministry-pathway">
        {TIERS.map((t) => (
          <div key={t.key}
            className={`mp-card${t.flagship ? " flagship" : ""}${t.dark ? " dark" : ""}${t.wide ? " wide" : ""}`}
            data-testid={`mp-tier-${t.key}`}>
            {t.flagship && <div className="mp-ribbon">Most Popular</div>}
            <h3>{t.name}</h3>
            {t.product && <div className="mp-product">{t.product}</div>}
            <div className={`mp-price${t.launching ? " soon" : ""}`}>{t.priceLine}</div>
            <p className="mp-audience">{t.audience}</p>
            <ul className="mp-includes">
              {t.includes.map((f) => <li key={f}>{f}</li>)}
            </ul>
            <div className="mp-cta-row">
              {t.interest
                ? <InterestForm user={user} />
                : <TierCTA tier={t} plans={plans} user={user} navigate={navigate}
                    busy={busy} setBusy={setBusy} setError={setError} />}
            </div>
          </div>
        ))}
      </div>

      <section className="mp-why" data-testid="why-churches-section">
        <h2>Why Churches Choose Liturgical Intelligence™</h2>
        <p className="mp-why-sub">Technology should preserve the wisdom of the Church—not replace it.</p>
        <div className="mp-why-grid">
          {WHY_CARDS.map(({ icon: Icon, title, body }) => (
            <div className="mp-why-card" key={title}>
              <Icon size={20} />
              <h4>{title}</h4>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mp-trust" data-testid="data-ownership-section">
        <Lock size={26} />
        <h2>Your Parish Owns Its Data</h2>
        <p>
          Every church retains complete ownership of its worship plans, Parish Memory™, reports,
          institutional knowledge, and future ministry data.
        </p>
        <p>Export your encrypted archive at any time.</p>
        <p className="mp-trust-strong">We never lock your ministry into our platform.</p>
      </section>

      <section className="mp-closing" data-testid="closing-section">
        <h2>Ready to Begin?</h2>
        <p>
          Whether you're planning one Sunday, preserving decades of parish memory, or supporting an
          entire diocese, Liturgical Intelligence™ was built to help churches remember, prepare,
          and lead with confidence.
        </p>
        <div className="mp-closing-btns">
          <button className="mp-btn gold" style={{ width: "auto" }}
            onClick={() => (user ? navigate("/worship-planner") : navigate("/register"))}
            data-testid="closing-start-btn">
            Start with SundayBuilder™
          </button>
          <button className="mp-btn ghost" style={{ width: "auto" }}
            onClick={() => navigate("/diocese")} data-testid="closing-conversation-btn">
            Schedule a Conversation
          </button>
        </div>
      </section>

      <section className="pricing-founding" data-testid="pricing-founding">
        <div>
          <div className="pricing-eyebrow gold"><Heart size={14} /> Support the mission</div>
          <h2>Join the Founding Circle</h2>
          <p>Help build the first institutional-memory platform for parish worship — and be recognized as a founding supporter.</p>
        </div>
        <button className="pricing-fc-btn" onClick={() => navigate("/founding-circle")} data-testid="pricing-founding-btn">Visit the Founding Circle</button>
      </section>

      <footer className="pricing-foot"><button onClick={() => navigate("/")}>← Back home</button></footer>
    </div>
  );
}
